import React, { useState, useEffect } from 'react';
import { 
  Sparkles, 
  Send, 
  Loader2, 
  CheckCircle2, 
  XCircle, 
  Trash2, 
  ArrowRightLeft, 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight,
  ArrowDownLeft,
  Package, 
  Plus,
  Image as ImageIcon, 
  Camera, 
  X, 
  WifiOff,
  FileText,
  AlertTriangle,
  FileSearch,
  Hash,
  User,
  Building2,
  Calendar,
  CreditCard,
  FileDigit,
  Receipt,
  Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { parseNaturalLanguageDocument, ParsedDocument, SmartInputMode, AIServiceError, FallbackRequest } from '../../services/aiService';
import { createManagedDocument, createArticle, createContact, generateUniqueLocatorCode } from '../../services/managementService';
import { AIFallbackModal } from '../AIFallbackModal';
import { createAccountingAccount } from '../../services/accountingService';
import { ManagedArticle, DocumentType, PaymentCondition, DocumentItem, ManagedContact, AccountingAccount, Transaction, ManagedDocument, Account, TransactionType, TransactionDetail } from '../../types';
import { calculateTransactionBalances } from '../../utils/transactionBalance';
import { useFirebase } from '../FirebaseProvider';
import { format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';
import { VoiceInput } from '../VoiceInput';
import { requestNotificationPermission, sendAppNotification } from '../../utils/notifications';
import { getSupabase } from '@potencial/supabase';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.mjs?url';
import { getInstitutionById } from '../../constants/institutions';

// Cargar el worker de pdf.js localmente en lugar de CDN
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

interface ManagementSmartInputProps {
  articles: ManagedArticle[];
  contacts: ManagedContact[];
  accountingAccounts: AccountingAccount[];
  transactions: Transaction[];
  documents: ManagedDocument[];
  accounts: Account[];
  onSuccess: () => void;
}

interface PreviewDocument extends ParsedDocument {
  linkedTransactionIds: string[];
  transactionIds?: string[]; // presente en runtime en el preview enriquecido (faltaba en el tipo)
  internalContactName?: string; // idem

  newMovements?: {
    id: string;
    accountId: string;
    amount: number;
    date: string;
  }[];
  applications?: { documentId: string; amount: number }[];
  isDuplicate?: boolean;
}

export const ManagementSmartInput: React.FC<ManagementSmartInputProps> = ({ 
  articles, 
  contacts, 
  accountingAccounts, 
  transactions,
  documents,
  accounts,
  onSuccess 
}) => {
  const { user, effectiveUserId } = useFirebase();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [mode, setMode] = useState<SmartInputMode>('PURCHASE_INVOICE');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [preview, setPreview] = useState<PreviewDocument[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<{ raw: File; data: string; mimeType: string, url: string, name: string }[]>([]);
  const [isMultipleDocuments, setIsMultipleDocuments] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const [fallbackRequest, setFallbackRequest] = useState<FallbackRequest | null>(null);
  const fallbackResolver = React.useRef<((confirmed: boolean) => void) | null>(null);

  const compressImage = async (file: File): Promise<Blob> => {
    if (file.type === 'application/pdf') {
       try {
          console.log(">>> [DEBUG] Extracting first page of PDF as Image to compress size...");
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const page = await pdf.getPage(1);
          
          // Render a ~1200px width equivalent if possible
          const viewport = page.getViewport({ scale: 2.0 });
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          
          if (!ctx) return file;
          
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          await page.render({ canvasContext: ctx, viewport } as any).promise;
          
          console.log(">>> [DEBUG] PDF page extracted successfully. Converting to WebP/JPEG.");
          
          // Re-compress the generated canvas down just like an image
          return new Promise((resolve) => {
             canvas.toBlob((blob) => {
                resolve(blob || file);
             }, 'image/jpeg', 0.8);
          });
       } catch (pdfErr) {
          console.error(">>> [DEBUG] Failed to extract PDF page to image fallback:", pdfErr);
          return file; // If PDF extraction fails, return the massive original PDF
       }
    }
    
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 1200;
        let width = img.width;
        let height = img.height;

        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(blob || file);
        }, 'image/jpeg', 0.7); // 0.7 quality is plenty for invoices
      };
    });
  };

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PY', { style: 'currency', currency: 'PYG' }).format(amount);
  };

  const getTransactionAmount = (t: Transaction) => {
    return t.details.reduce((sum, detail) => {
      return sum + (detail.type === 'CREDIT' ? detail.amount : -detail.amount);
    }, 0);
  };

  const transactionBalances = React.useMemo(() => {
    return calculateTransactionBalances(transactions, documents);
  }, [transactions, documents]);

  const getAvailableTransactionAmount = (tId: string, excludePreviewIdx?: number) => {
    const tInfo = transactionBalances[tId];
    if (!tInfo) return 0;
    
    // Lo que se está usando en otras previsualizaciones (para multi-linking simultáneo en esta sesión)
    const usedInPreview = preview
      .filter((p, i) => i !== excludePreviewIdx && p.linkedTransactionIds && p.linkedTransactionIds.includes(tId))
      .reduce((sum, p) => sum + p.totalAmount, 0); 
      
    return tInfo.availableAmount - usedInPreview;
  };

  const isTransactionAvailable = (tId: string, excludePreviewIdx?: number) => {
    return getAvailableTransactionAmount(tId, excludePreviewIdx) > 1; // 1 guaraní de margen por redondeos
  };

  const processFile = async (file: File) => {
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setStatus('error');
      setMessage('Por favor selecciona un archivo de imagen o PDF válido.');
      return;
    }
    
    if (file.type === 'application/pdf') {
       try {
          console.log(">>> [DEBUG] Extracting all pages of PDF as individual images...");
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const numPages = pdf.numPages;
          
          for (let i = 1; i <= numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 2.0 });
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            
            if (!ctx) continue;
            
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            
            await page.render({ canvasContext: ctx, viewport } as any).promise;
            
            const blob = await new Promise<Blob | null>((resolve) => {
               canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.8);
            });
            
            if (blob) {
               const pageFile = new File([blob], `${file.name}_pag${i}.jpg`, { type: 'image/jpeg' });
               const reader = new FileReader();
               reader.onloadend = () => {
                 const base64String = (reader.result as string).split(',')[1];
                 const newFileObj = {
                   raw: pageFile,
                   data: base64String,
                   mimeType: pageFile.type,
                   url: reader.result as string,
                   name: pageFile.name
                 };
                 setSelectedFiles(prev => [...prev, newFileObj]);
               };
               reader.readAsDataURL(pageFile);
            }
          }
          return; // Stop here, since we added all extracted pages instead of the PDF
       } catch (pdfErr) {
          console.error(">>> [DEBUG] Failed to extract PDF pages. Using fallback.", pdfErr);
       }
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64String = (reader.result as string).split(',')[1];
      const newFileObj = {
        raw: file,
        data: base64String,
        mimeType: file.type,
        url: reader.result as string,
        name: file.name
      };
      setSelectedFiles(prev => [...prev, newFileObj]);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach(processFile);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile();
        if (file) {
          e.preventDefault();
          processFile(file);
        }
      }
    }
  };

  const handleParse = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if ((!input.trim() && selectedFiles.length === 0) || !user) return;

    setLoading(true);
    setStatus('idle');
    setMessage('');
    
    try {
      requestNotificationPermission();
      
      const onFallback = (req: FallbackRequest): Promise<boolean> => {
        setFallbackRequest(req);
        return new Promise((resolve) => {
          fallbackResolver.current = resolve;
        });
      };

      const internalResponsibleHints: string[] = Array.from(new Set(
        documents
          .filter(d => d.internalContactName)
          .map(d => d.internalContactName)
      ));

      const parsed = await parseNaturalLanguageDocument(
        effectiveUserId!, 
        input, 
        articles, 
        contacts, 
        accountingAccounts, 
        mode, 
        selectedFiles.length > 0 ? selectedFiles.map(f => ({ data: f.data, mimeType: f.mimeType })) : undefined,
        onFallback,
        internalResponsibleHints,
        isMultipleDocuments
      );
      
      const enrichedPreview: PreviewDocument[] = parsed.map((doc, idx) => {
        let isDuplicate = false;
        if ((doc.type === 'PURCHASE_INVOICE' || doc.type === 'SALES_INVOICE') && doc.timbrado && doc.number) {
          isDuplicate = documents.some(d => 
            d.type === doc.type && 
            d.number === doc.number && 
            d.timbrado === doc.timbrado
          );
        }

        // 1. Find potential matches in transactions
        const potentialMatches = doc.condition === 'CONTADO' ? transactions
          .filter(t => t.type !== 'TRANSFER')
          .filter(t => isTransactionAvailable(t.id, idx)) // Unlinked transactions
          .filter(t => Math.abs(getTransactionAmount(t)) === doc.totalAmount)
          .map(t => t.id) : [];

        // 2. If it's a receipt, automatically try to link it to pending invoices of the same contact
        let autoApplications: { documentId: string; amount: number }[] = [];
        const isReceipt = doc.type === 'RECEIPT_EXPENSE' || doc.type === 'RECEIPT_INCOME';
        
        if (isReceipt && doc.contact?.id) {
          const relevantDocs = documents
            .filter(d => d.contactId === doc.contact?.id)
            .filter(d => {
              const directionMatch = doc.type === 'RECEIPT_EXPENSE'
                ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
              return directionMatch && (d.condition === 'CREDITO' || d.isHistorical) && d.remainingAmount > 0;
            })
            .sort((a, b) => a.date.localeCompare(b.date)); // Oldest first

          let remainingToApply = doc.totalAmount;
          for (const d of relevantDocs) {
            if (remainingToApply <= 0) break;
            const amountToApply = Math.min(d.remainingAmount, remainingToApply);
            if (amountToApply > 0) {
              autoApplications.push({ documentId: d.id, amount: amountToApply });
              remainingToApply -= amountToApply;
            }
          }
        }

        return {
          ...doc,
          linkedTransactionIds: potentialMatches,
          applications: autoApplications,
          isDuplicate
        };
      });

      sendAppNotification('Entrada Inteligente Completada', {
        body: 'El documento de gestión ha sido analizado por completo. Revisa la tabla de resultado.',
      });

      setPreview(enrichedPreview);
      if (enrichedPreview.length === 0) {
        setStatus('error');
        setMessage('No se detectaron documentos en la imagen o texto proveído. Intenta con una descripción más detallada.');
      }
    } catch (err: any) {
      console.error("AI Parse Error:", err);
      setStatus('error');
      
      sendAppNotification('Fallo en el Análisis', {
        body: err.message || "Error al procesar el documento.",
      });

      setMessage(err.message || "Error al procesar el documento.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!effectiveUserId || preview.length === 0) return;
    setRegistering(true);
    const startTime = Date.now();
    console.log(">>> [DEBUG] Starting handleConfirm process (Firestore Fallback Mode)");

    try {
      let baseUrls: string[] = [];
      if (selectedFiles && selectedFiles.length > 0) {
        setStatus('idle');
        setMessage(`Procesando ${selectedFiles.length} imagen(es) de respaldo...`);
        for (let i = 0; i < selectedFiles.length; i++) {
          const f = selectedFiles[i];
          console.log(`>>> [DEBUG] Starting file compression ${i+1}/${selectedFiles.length}`, { size: f.raw.size });
          
          const compressedBlob = await compressImage(f.raw);
          console.log(`>>> [DEBUG] Compression finished ${i+1}/${selectedFiles.length}`, { newSize: compressedBlob.size });

          let fileUrl = '';
          try {
            console.log(`>>> [DEBUG] Attempting to upload file ${i+1} to Supabase Storage...`);
            const ext = f.raw.type === 'application/pdf' ? 'pdf' : 'jpg';
            const objectPath = `users/${effectiveUserId}/documents/smart_input_${Date.now()}_${i}.${ext}`;
            const bucket = getSupabase().storage.from('finanzas-documents');

            const timeoutPromise = new Promise<never>((_, reject) => setTimeout(() => reject(new Error("Timeout de Supabase Storage.")), 8000));

            const uploadPromise = bucket
              .upload(objectPath, compressedBlob, { contentType: f.raw.type === 'application/pdf' ? 'application/pdf' : 'image/jpeg' })
              .then(({ error }) => {
                if (error) throw error;
              });

            await Promise.race([uploadPromise, timeoutPromise]);

            fileUrl = bucket.getPublicUrl(objectPath).data.publicUrl;
            console.log(`>>> [DEBUG] Successfully uploaded file ${i+1} to Storage!`);
          } catch (storageErr: any) {
            console.error(`>>> [DEBUG] Supabase Storage failed for file ${i+1}. Falling back to Base64.`, storageErr);
            if (compressedBlob.size > 800000) {
               throw new Error(`El archivo ${i+1} supera el límite (1MB) y falló la carga a Storage. Por favor, asegúrate de que el peso de cada archivo sea menor o haz capturas.`);
            }
            fileUrl = await new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(compressedBlob);
            });
            console.log(`>>> [DEBUG] Image ${i+1} converted to DataURL (Base64)`);
          }
          baseUrls.push(fileUrl);
        }
      }
      const baseDocumentUrl = baseUrls.join('|');

      // Cachés temporales para evitar creaciones duplicadas en la misma sesión de guardado
      const resolvedAccounts: Record<string, string> = {}; // suggestionName -> id
      const resolvedContacts: Record<string, string> = {}; // contactName -> id
      const resolvedArticles: Record<string, string> = {}; // articleName -> id
      const generatedCodes: string[] = [];

      for (const [idx, parsed] of preview.entries()) {
        const docStartTime = Date.now();
        const docCode = await generateUniqueLocatorCode(effectiveUserId);
        generatedCodes.push(docCode);

        const isExpense = parsed.type === 'PURCHASE_INVOICE' || parsed.type === 'INTERNAL_EXPENSE' || parsed.type === 'RECEIPT_EXPENSE';
        
        // 1. Resolve Contact (Initialize keys first to avoid Lexical error)
        const contactKey = parsed.contact?.name || `Contacto_${Date.now()}`;
        const intContactKey = parsed.internalContact?.name || `Interno_${Date.now()}`;

        let newTransactionsData: any[] = [];
        if (parsed.condition !== 'CREDITO' && !parsed.isHistorical) {
          newTransactionsData = (parsed.newMovements || []).map(m => ({
            description: `${parsed.type?.replace('_', ' ') || mode.replace('_', ' ')}: ${contactKey} - ${parsed.number || docCode}`,
            type: isExpense ? 'EXPENSE' as TransactionType : 'INCOME' as TransactionType,
            date: m.date,
            details: [
              { 
                accountId: m.accountId, 
                amount: m.amount, 
                type: isExpense ? 'DEBIT' as const : 'CREDIT' as const 
              }
            ]
          }));
        }

        setStatus('idle');
        setMessage(`Registrando documento ${idx + 1}/${preview.length} de ${contactKey}...`);
        console.log(`>>> [DEBUG] Processing document ${idx + 1}:`, contactKey);

        let contactId = parsed.contact?.id;

        if (parsed.contact?.isNew) {
          if (resolvedContacts[contactKey]) {
            contactId = resolvedContacts[contactKey];
          } else {
            const newContact = await createContact(effectiveUserId, {
              name: contactKey,
              taxIdType: (parsed.contact.taxId && parsed.contact.taxId !== '') ? 'RUC' : 'SIN NOMBRE',
              taxId: parsed.contact.taxId || '',
              isCustomer: mode === 'SALES_INVOICE' || mode === 'INTERNAL_INCOME',
              isProvider: mode === 'PURCHASE_INVOICE' || mode === 'INTERNAL_EXPENSE'
            });
            contactId = newContact.id;
            resolvedContacts[contactKey] = contactId;
          }
        }

        // 2. Resolve Internal Contact
        let internalContactId = parsed.internalContact?.id;

        if (parsed.internalContact?.isNew) {
          if (resolvedContacts[intContactKey]) {
            internalContactId = resolvedContacts[intContactKey];
          } else {
            const newIntContact = await createContact(effectiveUserId, {
              name: intContactKey,
              taxIdType: 'SIN NOMBRE',
              taxId: '',
              isCustomer: false,
              isProvider: false
            });
            internalContactId = newIntContact.id;
            resolvedContacts[intContactKey] = internalContactId;
          }
        }

        // 3. Resolve Items and Articles
        const items: DocumentItem[] = [];
        for (const pItem of (parsed.items || [])) {
          let articleId = pItem.articleId;
          let accId = pItem.suggestedAccountingAccountId;
          const articleKey = pItem.articleName || `Articulo_${Date.now()}`;

          // If it's a new account suggestion and user is confirming, resolve it
          if (pItem.newAccountSuggestion) {
            const accKey = pItem.newAccountSuggestion.name;
            if (resolvedAccounts[accKey]) {
              accId = resolvedAccounts[accKey];
            } else {
              // Limpiar la categoría provista por la IA (ej: 'EGRESOS' -> 'EGRESO')
              let safeCategory = pItem.newAccountSuggestion.category.toUpperCase().trim();
              if (safeCategory.endsWith('S')) safeCategory = safeCategory.slice(0, -1);
              if (!['ACTIVO', 'PASIVO', 'PATRIMONIO', 'INGRESO', 'EGRESO'].includes(safeCategory)) {
                 safeCategory = 'EGRESO'; // Fallback seguro
              }

              const newAcc = await createAccountingAccount(effectiveUserId, {
                name: pItem.newAccountSuggestion.name,
                category: safeCategory as any,
                parentId: pItem.newAccountSuggestion.parentId,
                code: 'AUTO-' + Math.floor(Math.random() * 10000), // Firebase Rules: size() > 0 requirement
                isSelectable: true,
                level: 1, 
                initialBalance: 0
              });
              accId = newAcc.id;
              resolvedAccounts[accKey] = accId;
            }
          }

          if (pItem.isNewArticle) {
            if (resolvedArticles[articleKey]) {
              articleId = resolvedArticles[articleKey];
            } else {
              const newArticle = await createArticle(effectiveUserId, {
                name: articleKey,
                description: '',
                category: 'General',
                accountingAccountId: accId || ''
              });
              articleId = newArticle.id;
              resolvedArticles[articleKey] = articleId;
            }
          }

          items.push({
            articleId: articleId || '',
            quantity: pItem.quantity || 0,
            unitPrice: pItem.unitPrice || 0,
            foreignUnitPrice: pItem.foreignUnitPrice != null ? Number(pItem.foreignUnitPrice) : undefined,
            total: pItem.total || 0,
            foreignTotal: pItem.foreignTotal != null ? Number(pItem.foreignTotal) : undefined,
            accountingAccountId: accId || ''
          });
        }

        // Sanitize outputted date to ensure exact conformity to YYYY-MM-DD
        let finalDate = format(new Date(), 'yyyy-MM-dd');
        try {
           const parsedDateTest = new Date(parsed.date + "T12:00:00");
           if (isValid(parsedDateTest)) {
             finalDate = format(parsedDateTest, 'yyyy-MM-dd');
           }
        } catch(e) {
           // Fallback to today if ai failed terribly on the text format
        }

        // 4. Distribute document total across linked transactions ONLY IF CONTADO
        let remainingDocTotal = parsed.totalAmount || 0;
        let finalTransactionIds = [...(parsed.transactionIds || [])];

        if (parsed.condition !== 'CREDITO' && !parsed.isHistorical) {
          // First handle existing linked transactions
          for (const tId of (parsed.linkedTransactionIds || [])) {
            if (remainingDocTotal <= 1) break;
            const t = transactions.find((tx: any) => tx.id === tId);
            if (!t) continue;
            
            const available = getAvailableTransactionAmount(tId, idx);
            const toAssign = Math.min(available, remainingDocTotal);
            
            if (toAssign > 0) {
              remainingDocTotal -= toAssign;
              if (!finalTransactionIds.includes(tId)) {
                 finalTransactionIds.push(tId);
              }
            }
          }
        } else {
          // Si es CREDITO o Historico, NO vinculamos movimientos directamente acá.
          // El pago se registra luego por separado.
          finalTransactionIds = [];
        }

        await createManagedDocument(effectiveUserId, {
          type: (parsed.type || 'PURCHASE_INVOICE') as DocumentType,
          date: finalDate,
          number: parsed.number || '',
          timbrado: parsed.timbrado || undefined,
          cdc: parsed.cdc || undefined,
          locatorCode: docCode,
          contactId: contactId || 'UNKNOWN',
          contactName: contactKey,
          internalContactId: internalContactId || 'UNKNOWN',
          internalContactName: intContactKey,
          condition: parsed.isHistorical ? 'CREDITO' : (parsed.condition || 'CONTADO'),
          totalAmount: parsed.totalAmount || 0,
          currency: parsed.currency || 'PYG',
          exchangeRate: parsed.exchangeRate || 1,
          foreignTotalAmount: parsed.foreignTotalAmount != null ? Number(parsed.foreignTotalAmount) : undefined,
          tax5Amount: parsed.tax5Amount || 0,
          tax10Amount: parsed.tax10Amount || 0,
          isHistorical: !!parsed.isHistorical,
          historicalPendingAmount: parsed.historicalPendingAmount || 0,
          baseDocumentUrl: baseDocumentUrl || undefined,
          observations: parsed.observations || '',
          items,
          isCancelled: !parsed.isHistorical && (parsed.condition === 'CONTADO' || (parsed.linkedTransactionIds && parsed.linkedTransactionIds.length > 0) || (parsed.newMovements && parsed.newMovements.length > 0)),
          remainingAmount: parsed.isHistorical 
            ? (parsed.historicalPendingAmount || 0)
            : (parsed.condition === 'CREDITO' && (!parsed.linkedTransactionIds || parsed.linkedTransactionIds.length === 0) && (!parsed.newMovements || parsed.newMovements.length === 0)) ? (parsed.totalAmount || 0) : 0,
          transactionIds: finalTransactionIds,
          applications: parsed.applications
        }, newTransactionsData);
        console.log(`>>> [DEBUG] Document ${idx + 1} finalized in ${Date.now() - docStartTime}ms`);
      }

      console.log(`>>> [DEBUG] TOTAL PROCESS COMPLETED in ${Date.now() - startTime}ms`);
      setStatus('success');
      
      const codesMessage = generatedCodes.length > 0 
        ? `Códigos asignados: ${generatedCodes.join(', ')}`
        : '';
        
      sendAppNotification('Guardado Completado', {
        body: `¡${preview.length} documentos registrados con éxito! ${codesMessage}`,
      });

      setMessage(`¡${preview.length} documentos registrados con éxito! ${codesMessage}\nEscribe estos códigos en los documentos físicos.`);
      setPreview([]);
      setInput('');
      setSelectedFiles([]);
      setIsMultipleDocuments(false);
      setTimeout(() => {
        setStatus('idle');
      }, 15000);
    } catch (err: any) {
      setStatus('error');
      
      sendAppNotification('Fallo al Guardar', {
        body: err.message || "Error al registrar los documentos.",
      });

      setMessage(err.message || "Error al registrar los documentos.");
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto">
      <div className="bg-zinc-900 border border-zinc-800 rounded-3xl p-8 shadow-2xl relative overflow-hidden">
        {!isOnline && (
          <div className="absolute inset-0 bg-zinc-950/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center text-center p-6">
            <WifiOff className="text-amber-500 mb-4" size={48} />
            <h3 className="text-xl font-bold text-white mb-2">Sin Conexión</h3>
            <p className="text-sm text-zinc-400">La entrada inteligente requiere internet.</p>
          </div>
        )}
        
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-indigo-500/10 rounded-2xl flex items-center justify-center border border-indigo-500/20">
            <Sparkles className="text-indigo-400" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-bold text-zinc-100">Carga Inteligente de Documentos</h3>
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold">Procesa facturas o recibos con IA</p>
          </div>
        </div>

        <div className="mb-6 space-y-3">
          <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">
            ¿Qué tipo de documento vas a procesar ahora?
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {[
              { id: 'PURCHASE_INVOICE', label: 'Factura Compra', icon: ArrowDownLeft, color: 'text-red-400', bg: 'bg-red-400/10' },
              { id: 'SALES_INVOICE', label: 'Factura Venta', icon: ArrowUpRight, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
              { id: 'RECEIPT_EXPENSE', label: 'Recibo Egreso', icon: Receipt, color: 'text-red-400', bg: 'bg-red-400/10' },
              { id: 'RECEIPT_INCOME', label: 'Recibo Ingreso', icon: Receipt, color: 'text-emerald-400', bg: 'bg-emerald-400/10' },
              { id: 'INTERNAL_EXPENSE', label: 'Doc. Egreso', icon: FileDigit, color: 'text-zinc-400', bg: 'bg-zinc-400/10' },
              { id: 'INTERNAL_INCOME', label: 'Doc. Ingreso', icon: FileDigit, color: 'text-zinc-400', bg: 'bg-zinc-400/10' },
            ].map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setMode(m.id as SmartInputMode)}
                className={`flex flex-col items-center justify-center p-3 rounded-2xl border transition-all ${
                  mode === m.id 
                    ? `bg-zinc-800 border-zinc-600 shadow-lg ${m.color}` 
                    : 'bg-zinc-950 border-zinc-800 text-zinc-500 hover:border-zinc-700'
                }`}
              >
                <m.icon size={18} className="mb-1" />
                <span className="text-[10px] font-bold uppercase">{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleParse} className="space-y-4">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={handlePaste}
            placeholder={`Describe los detalles de la ${mode.replace('_', ' ').toLowerCase()} o sube el archivo para extraer los datos...`}
            disabled={loading || registering || !isOnline}
            rows={3}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-2xl px-6 py-4 text-sm focus:ring-2 focus:ring-indigo-500/50 outline-none transition-all placeholder:text-zinc-700 resize-none"
          />

          <div className="flex flex-wrap items-center gap-3">
            <label className="flex items-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl cursor-pointer transition-all border border-zinc-700 text-sm font-bold">
              <ImageIcon size={18} className="text-indigo-400" />
              Sube Imagen o PDF
              <input type="file" multiple accept="image/*,application/pdf" onChange={handleFileChange} className="hidden" />
            </label>
            <label className="flex items-center gap-2 px-4 py-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl cursor-pointer transition-all border border-zinc-700 text-sm font-bold">
              <Camera size={18} className="text-indigo-400" />
              Cámara
              <input type="file" accept="image/*" capture="environment" onChange={handleFileChange} className="hidden" />
            </label>

            {selectedFiles.map((fileObj, idx) => (
              <div key={idx} className="flex items-center gap-2 bg-zinc-800 px-3 py-2 rounded-xl border border-indigo-500/30">
                {fileObj.mimeType === 'application/pdf' ? (
                  <FileText className="text-red-400" size={20} />
                ) : (
                  <img src={fileObj.url} className="w-8 h-8 rounded-lg object-cover" />
                )}
                <div className="flex flex-col">
                   <span className="text-[10px] text-zinc-300 font-bold truncate max-w-[100px]">{fileObj.name || 'Archivo'}</span>
                   <span className="text-[8px] text-zinc-500 uppercase">{fileObj.mimeType.split('/')[1]}</span>
                </div>
                <button onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== idx))} className="p-1 hover:bg-zinc-700 rounded-lg text-zinc-500">
                  <X size={14} />
                </button>
              </div>
            ))}

            <div className="flex-1" />

            {selectedFiles.length > 1 && (
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer hover:text-zinc-300 mr-2 border border-zinc-800 px-3 py-2 rounded-xl">
                <input 
                  type="checkbox" 
                  checked={isMultipleDocuments}
                  onChange={(e) => setIsMultipleDocuments(e.target.checked)}
                  className="rounded border-zinc-700 text-indigo-500 focus:ring-indigo-500 bg-zinc-900"
                />
                <span className="text-[10px] font-bold">Documentos separados</span>
              </label>
            )}

            <button
              type="submit"
              disabled={loading || registering || (!input.trim() && selectedFiles.length === 0)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-xl font-black transition-all flex items-center gap-2 shadow-lg shadow-indigo-600/20 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={20} /> : <Send size={20} />}
              PROCESAR
            </button>
          </div>
        </form>

        <AnimatePresence>
          {status !== 'idle' && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className={`mt-6 p-4 rounded-2xl border flex items-center gap-3 font-bold text-sm ${status === 'success' ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>
              {status === 'success' ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
              <div className="flex flex-col gap-1 w-full">
                <span>{message}</span>
                {uploadProgress !== null && (
                  <div className="w-full bg-zinc-800 rounded-full h-1.5 mt-1 overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${uploadProgress}%` }}
                      className="bg-indigo-500 h-full"
                    />
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {preview.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-zinc-900 border border-zinc-800 rounded-3xl overflow-hidden shadow-2xl">
            <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
              <h4 className="font-bold text-white flex items-center gap-2">
                <FileText size={20} className="text-indigo-400" />
                Vista Previa de Documentos ({preview.length})
              </h4>
              <button onClick={() => setPreview([])} className="text-xs text-zinc-500 hover:text-white">Descartar</button>
            </div>
            
            <div className="divide-y divide-zinc-800">
              {preview.map((doc, idx) => {
                const isReceipt = doc.type === 'RECEIPT_EXPENSE' || doc.type === 'RECEIPT_INCOME';
                return (
                <div key={idx} className={`p-8 space-y-6 ${doc.isDuplicate ? 'bg-red-500/5 relative' : 'relative'}`}>
                  <div className="absolute top-4 right-4 flex items-center gap-2">
                    {doc.isDuplicate && (
                      <div className="bg-red-500 text-white text-[10px] font-bold px-2 py-1 rounded-md flex items-center gap-1 shadow-lg shadow-red-500/20">
                        <AlertTriangle size={12} /> DUPLICADO
                      </div>
                    )}
                    <button 
                      onClick={() => setPreview(preview.filter((_, i) => i !== idx))}
                      className="p-1.5 bg-zinc-800 hover:bg-red-500/20 hover:text-red-400 text-zinc-500 rounded-lg transition-all"
                      title="Descartar este documento"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">
                    <div className="flex items-center gap-5">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center border shadow-inner ${doc.type?.includes('INCOME') || doc.type?.includes('SALES') || doc.type === 'RECEIPT_INCOME' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}`}>
                         {doc.type?.includes('INCOME') || doc.type?.includes('SALES') || doc.type === 'RECEIPT_INCOME' ? <ArrowUpRight size={28} /> : <ArrowDownLeft size={28} />}
                      </div>
                      <div className="space-y-1">
                        <div className="text-xl font-black text-white flex items-center gap-3">
                          {doc.contact?.name || 'Contacto Desconocido'}
                          {doc.contact?.isNew && (
                            <span className="text-[9px] bg-amber-500/10 text-amber-500 px-2 py-0.5 rounded-full border border-amber-500/20 flex items-center gap-1">
                              <Plus size={10} /> Nuevo Contacto
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                           <span className="text-[10px] bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">{doc.type?.split('_').join(' ') || 'DESCONOCIDO'}</span>
                           {!isReceipt && (
                             <span className="text-[10px] bg-indigo-500/10 text-indigo-400 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider">{doc.condition || 'DESCONOCIDO'}</span>
                           )}
                           {doc.isHistorical && (
                             <span className="text-[10px] bg-indigo-500/20 text-indigo-400 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider flex items-center gap-1 border border-indigo-500/30">
                               <Clock size={10} /> Histórico
                             </span>
                           )}
                           <span className="text-[10px] bg-zinc-800 text-zinc-500 px-2 py-0.5 rounded-md font-bold uppercase tracking-wider flex items-center gap-1">
                             <Calendar size={10} /> {doc.date || 'PENDIENTE'}
                           </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col items-end gap-1">
                      <div className="text-3xl font-black text-white">{formatCurrency(doc.totalAmount || 0)}</div>
                      <div className="flex gap-3 mt-1">
                        {doc.tax5Amount !== undefined && doc.tax5Amount > 0 && (
                          <div className="text-[9px] font-bold text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded border border-zinc-700">IVA 5%: {formatCurrency(doc.tax5Amount)}</div>
                        )}
                        {doc.tax10Amount !== undefined && doc.tax10Amount > 0 && (
                          <div className="text-[9px] font-bold text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded border border-zinc-700">IVA 10%: {formatCurrency(doc.tax10Amount)}</div>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mt-1">
                        {isReceipt 
                          ? `${(doc.applications?.length || 0)} DOCS. APLICADOS`
                          : `${(doc.items?.length || 0)} LÍNEAS DETECTADAS`
                        }
                      </div>
                    </div>
                  </div>

                  {/* New Financial Movements Section */}
                  {!doc.isHistorical && doc.condition === 'CONTADO' && (
                    <div className="p-4 bg-emerald-500/5 rounded-2xl border border-emerald-500/20 space-y-4">
                      <div className="flex items-center justify-between">
                         <label className="text-[10px] font-bold text-emerald-400 uppercase tracking-widest flex items-center gap-2">
                           <TrendingUp size={14} /> Registrar Pagos / Cobros Directos
                         </label>
                         <button
                           onClick={() => {
                             const newPreview = [...preview];
                             const docRef = newPreview[idx];
                             const currentLinked = docRef.linkedTransactionIds.reduce((sum, id) => {
                               const t = transactions.find(tx => tx.id === id);
                               return sum + (t ? Math.abs(getTransactionAmount(t)) : 0);
                             }, 0);
                             const currentNew = (docRef.newMovements || []).reduce((sum, m) => sum + m.amount, 0);
                             const left = (docRef.totalAmount || 0) - currentLinked - currentNew;

                             docRef.newMovements = [
                               ...(docRef.newMovements || []),
                               {
                                 id: Math.random().toString(36).substr(2, 9),
                                 accountId: accounts[0]?.id || '',
                                 amount: left > 0 ? left : 0,
                                 date: doc.date || format(new Date(), 'yyyy-MM-dd')
                               }
                             ];
                             setPreview(newPreview);
                           }}
                           className="text-[10px] font-black text-emerald-500 hover:text-emerald-400 uppercase tracking-tighter flex items-center gap-1"
                         >
                           <Plus size={12} /> Agregar Pago
                         </button>
                      </div>

                      {(doc.newMovements || []).length > 0 && (
                        <div className="space-y-3">
                          {doc.newMovements!.map((m, mIdx) => (
                            <div key={m.id} className="grid grid-cols-1 md:grid-cols-4 gap-3 bg-zinc-950 p-3 rounded-xl border border-zinc-900 group">
                              <div className="md:col-span-2">
                                <select
                                  value={m.accountId}
                                  onChange={(e) => {
                                    const newPreview = [...preview];
                                    newPreview[idx].newMovements![mIdx].accountId = e.target.value;
                                    setPreview(newPreview);
                                  }}
                                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-300"
                                >
                                  <option value="">Seleccionar cuenta...</option>
                                  {accounts.map(a => {
                                    const institution = a.institutionId ? getInstitutionById(a.institutionId) : null;
                                    const instName = institution ? ` - ${institution.name}` : '';
                                    return (
                                      <option key={a.id} value={a.id}>
                                        {a.name}{instName} ({formatCurrency(a.balance)})
                                      </option>
                                    );
                                  })}
                                </select>
                              </div>
                              <div className="relative">
                                <input
                                  type="number"
                                  value={m.amount}
                                  onChange={(e) => {
                                    const newPreview = [...preview];
                                    newPreview[idx].newMovements![mIdx].amount = parseFloat(e.target.value) || 0;
                                    setPreview(newPreview);
                                  }}
                                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-right font-bold text-emerald-400 pr-2"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="date"
                                  value={m.date}
                                  onChange={(e) => {
                                    const newPreview = [...preview];
                                    newPreview[idx].newMovements![mIdx].date = e.target.value;
                                    setPreview(newPreview);
                                  }}
                                  className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-[10px] text-zinc-400"
                                />
                                <button
                                  onClick={() => {
                                    const newPreview = [...preview];
                                    newPreview[idx].newMovements = newPreview[idx].newMovements!.filter((_, i) => i !== mIdx);
                                    setPreview(newPreview);
                                  }}
                                  className="p-2 text-zinc-600 hover:text-red-500 transition-colors"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {(!doc.newMovements || doc.newMovements.length === 0) && (
                        <p className="text-[10px] text-zinc-600 text-center py-2 italic font-medium">No se han registrado movimientos de dinero para este documento.</p>
                      )}
                    </div>
                  )}

                  {/* Document Specific Info (Paraguay Tax Fields) */}
                  {(doc.number || doc.timbrado || doc.cdc || doc.internalContactName) && (
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-zinc-950/50 rounded-2xl border border-zinc-800">
                      {doc.number && (
                        <div className="space-y-1">
                          <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest flex items-center gap-1"><Hash size={10} /> Nro. Comprobante</label>
                          <div className="text-xs text-zinc-300 font-mono">{doc.number}</div>
                        </div>
                      )}
                      {doc.timbrado && (
                        <div className="space-y-1">
                          <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest flex items-center gap-1"><FileDigit size={10} /> Timbrado</label>
                          <div className="text-xs text-zinc-300 font-mono">{doc.timbrado}</div>
                        </div>
                      )}
                      {doc.internalContact?.name && (
                        <div className="space-y-1">
                          <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest flex items-center gap-1"><Building2 size={10} /> Responsable Int.</label>
                          <div className="text-xs font-bold text-zinc-300 flex items-center gap-2">
                            {doc.internalContact.name}
                            {doc.internalContact.isNew && (
                               <span className="text-[8px] bg-amber-500/10 text-amber-500 px-1 rounded border border-amber-500/10">NUEVO</span>
                            )}
                          </div>
                        </div>
                      )}
                      {doc.cdc && (
                        <div className="space-y-1 col-span-2 lg:col-span-1">
                          <label className="text-[9px] font-bold text-zinc-600 uppercase tracking-widest flex items-center gap-1"><FileSearch size={10} /> CDC (E-Factura)</label>
                          <div className="text-[10px] text-emerald-500/70 font-mono truncate" title={doc.cdc}>{doc.cdc}</div>
                        </div>
                      )}
                    </div>
                  )}

                   {/* Historical / Initial Balance Toggle */}
                   {!isReceipt && (
                   <div className="p-4 bg-indigo-500/5 rounded-2xl border border-indigo-500/20 flex flex-col md:flex-row items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            const newPreview = [...preview];
                            newPreview[idx].isHistorical = !newPreview[idx].isHistorical;
                            if (newPreview[idx].isHistorical) {
                              newPreview[idx].condition = 'CREDITO';
                              newPreview[idx].linkedTransactionIds = [];
                              if (!newPreview[idx].historicalPendingAmount) {
                                newPreview[idx].historicalPendingAmount = newPreview[idx].totalAmount;
                              }
                            }
                            setPreview(newPreview);
                          }}
                          className={`w-10 h-6 rounded-full transition-all relative ${doc.isHistorical ? 'bg-indigo-600' : 'bg-zinc-700'}`}
                        >
                          <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${doc.isHistorical ? 'left-5' : 'left-1'}`} />
                        </button>
                        <div>
                          <p className="text-sm font-bold text-white">¿Es una carga histórica / saldo inicial?</p>
                          <p className="text-[10px] text-zinc-500 italic">No generará asientos contables y te permite fijar el saldo pendiente actual.</p>
                        </div>
                      </div>

                      {doc.isHistorical && (
                        <div className="flex items-center gap-3 bg-zinc-950 p-2 rounded-xl border border-zinc-800">
                          <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest whitespace-nowrap">Saldo Pendiente Hoy:</label>
                          <input
                            type="number"
                            value={doc.historicalPendingAmount || 0}
                            onChange={(e) => {
                              const newPreview = [...preview];
                              newPreview[idx].historicalPendingAmount = parseFloat(e.target.value);
                              setPreview(newPreview);
                            }}
                            className="w-32 bg-transparent text-sm font-bold text-white outline-none text-right"
                          />
                        </div>
                      )}
                   </div>
                   )}

                   {/* Transaction Linking Section */}
                   {!doc.isHistorical && doc.condition === 'CONTADO' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between px-1">
                        <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                          <CreditCard size={10} /> Movimientos de Pago / Cobro
                        </label>
                        {doc.linkedTransactionIds.length > 0 && (
                          <span className="text-[9px] font-bold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                            {doc.linkedTransactionIds.length} VINCULADOS
                          </span>
                        )}
                      </div>

                      <div className="bg-zinc-800/30 rounded-2xl border border-zinc-800 p-4 space-y-3">
                        <p className="text-[10px] text-zinc-500 italic">
                          Selecciona los movimientos bancarios o de caja que cancelan este documento:
                        </p>
                        
                        <div className="grid gap-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
                          {transactions
                            .filter(t => t.type !== 'TRANSFER')
                            .filter(t => isTransactionAvailable(t.id, idx) || doc.linkedTransactionIds.includes(t.id)) // Libres o ya seleccionados
                            .filter(t => {
                              const tAmount = getTransactionAmount(t);
                              // Mostrar solo gastos para facturas de compra y viceversa
                              const isDocExpenseValue = doc.type === 'PURCHASE_INVOICE' || doc.type === 'INTERNAL_EXPENSE' || doc.type === 'RECEIPT_EXPENSE';
                              return isDocExpenseValue ? tAmount < 0 : tAmount > 0;
                            })
                            .sort((a, b) => {
                              const aSelected = doc.linkedTransactionIds.includes(a.id);
                              const bSelected = doc.linkedTransactionIds.includes(b.id);
                              
                              // 1. Mostrar elementos seleccionados arriba de todo
                              if (aSelected && !bSelected) return -1;
                              if (!aSelected && bSelected) return 1;
                              
                              // 2. Ordenar por cercanía del monto del documento (diferencia absoluta)
                              const aAmount = Math.abs(getTransactionAmount(a));
                              const bAmount = Math.abs(getTransactionAmount(b));
                              const docTotal = Math.abs(doc.totalAmount || 0);
                              
                              const aDiff = Math.abs(aAmount - docTotal);
                              const bDiff = Math.abs(bAmount - docTotal);
                              
                              // Menor diferencia primero (más cercanos en monto)
                              return aDiff - bDiff;
                            })
                            .map(transaction => {
                              const isSelected = doc.linkedTransactionIds.includes(transaction.id);
                              const tAmount = getTransactionAmount(transaction);
                              return (
                                <button
                                  key={transaction.id}
                                  onClick={() => {
                                    const newPreview = [...preview];
                                    const docToUpdate = newPreview[idx];
                                    if (isSelected) {
                                      docToUpdate.linkedTransactionIds = docToUpdate.linkedTransactionIds.filter(id => id !== transaction.id);
                                    } else {
                                      docToUpdate.linkedTransactionIds = [...docToUpdate.linkedTransactionIds, transaction.id];
                                    }
                                    setPreview(newPreview);
                                  }}
                                  className={`flex items-center justify-between p-3 rounded-xl border transition-all text-left ${
                                    isSelected 
                                      ? 'bg-emerald-500/10 border-emerald-500/50 text-white' 
                                      : 'bg-zinc-900 border-zinc-700 text-zinc-400 hover:border-zinc-500'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isSelected ? 'bg-emerald-500 text-black' : 'bg-zinc-800 text-zinc-500 uppercase font-black text-[10px]'}`}>
                                      {isSelected ? <CheckCircle2 size={16} /> : transaction.type.substring(0, 1)}
                                    </div>
                                    <div>
                                      <div className="text-[11px] font-bold line-clamp-1">{transaction.description}</div>
                                      <div className="text-[9px] opacity-60 font-mono">{format(new Date(transaction.date), 'dd/MM/yy', { locale: es })}</div>
                                    </div>
                                  </div>
                                  <div className={`text-right font-mono ${isSelected ? 'text-emerald-400' : ''}`}>
                                    <div className="text-xs font-bold">
                                      {formatCurrency(Math.abs(tAmount))}
                                    </div>
                                    <div className="text-[9px] opacity-60">
                                      D: {formatCurrency(getAvailableTransactionAmount(transaction.id, isSelected ? undefined : idx))}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          {transactions.filter(t => t.type !== 'TRANSFER' && isTransactionAvailable(t.id, idx)).length === 0 && (
                            <div className="text-center py-4">
                              <p className="text-[10px] text-zinc-600">No hay movimientos pendientes para vincular.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                   )}

                  {/* Items List / Applications List */}
                  {!isReceipt ? (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-zinc-500 uppercase tracking-widest px-1">Detalle de ítems</label>
                      <div className="bg-zinc-800/30 rounded-2xl border border-zinc-800 overflow-hidden">
                        <table className="w-full text-left text-xs">
                          <thead>
                            <tr className="bg-zinc-800/50 text-zinc-500 font-bold uppercase tracking-wider">
                              <th className="px-4 py-2">Artículo</th>
                              <th className="px-4 py-2 text-center">Cant.</th>
                              <th className="px-4 py-2 text-right">Unitario</th>
                              <th className="px-4 py-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-zinc-800">
                            {(doc.items || []).map((item, iIdx) => (
                              <tr key={iIdx} className="text-zinc-300">
                                <td className="px-4 py-3">
                                  <div className="font-medium flex items-center gap-2">
                                    {item.articleName}
                                    {item.isNewArticle && (
                                      <span className="text-[8px] bg-indigo-500/20 text-indigo-400 px-1.5 py-0.5 rounded border border-indigo-500/20 font-bold uppercase">Nuevo Art.</span>
                                    )}
                                  </div>
                                  {(item.suggestedAccountingAccountId || item.newAccountSuggestion) && (
                                    <div className="text-[9px] mt-1 flex items-center gap-1 text-zinc-500">
                                      <ArrowRightLeft size={10} className="text-indigo-400/50" />
                                      <span>Asignado a: </span>
                                      {item.newAccountSuggestion ? (
                                        <span className="text-amber-400 flex items-center gap-1">
                                          [PROPUESTA: {item.newAccountSuggestion.name}]
                                          <span className="text-[8px] opacity-70">En: {item.newAccountSuggestion.parentName}</span>
                                        </span>
                                      ) : (
                                        <span className="italic">
                                          {accountingAccounts.find(a => a.id === item.suggestedAccountingAccountId)?.name || 'Cuenta no encontrada'}
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">{item.quantity}</td>
                                <td className="px-4 py-3 text-right">{formatCurrency(item.unitPrice)}</td>
                                <td className="px-4 py-3 text-right font-bold text-white">{formatCurrency(item.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 bg-emerald-500/5 border border-emerald-500/20 rounded-3xl p-6">
                      <label className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest px-1 flex items-center gap-2">
                         <FileSearch size={12} /> Facturas / Documentos Pendientes {doc.contact?.name ? `de ${doc.contact?.name}` : ''}
                      </label>
                      <div className="bg-zinc-950/80 rounded-2xl border border-zinc-800/50 flex flex-col p-4">
                        {!doc.contact?.id && (
                           <div className="text-center py-4">
                             <p className="text-xs text-zinc-500 italic">No se reconoció el contacto automáticamente. Busca la factura abajo.</p>
                           </div>
                        )}
                        <div className="space-y-2">
                           {doc.contact?.id && documents
                              .filter(d => {
                                const isCorrectDirection = doc.type === 'RECEIPT_EXPENSE' 
                                  ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                                  : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                                
                                return (d.condition === 'CREDITO' || d.isHistorical) && 
                                  d.remainingAmount > 0 && 
                                  isCorrectDirection &&
                                  d.contactId === doc.contact?.id;
                              })
                              .map(d => {
                                const app = (doc.applications || []).find(a => a.documentId === d.id);
                                const isSelected = !!app;

                                return (
                                  <div 
                                    key={d.id} 
                                    className={`p-3 rounded-2xl border transition-all flex flex-col md:flex-row md:items-center gap-3 ${
                                      isSelected ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-950/50 border-zinc-800'
                                    }`}
                                  >
                                    <div className="flex-1 flex items-center gap-3">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          const newPreview = [...preview];
                                          let apps = newPreview[idx].applications || [];
                                          if (isSelected) {
                                            apps = apps.filter(a => a.documentId !== d.id);
                                          } else {
                                            apps.push({ documentId: d.id, amount: d.remainingAmount });
                                          }
                                          newPreview[idx].applications = apps;
                                          
                                          // Recalculate totalAmount based on applications
                                          const newTotal = apps.reduce((sum, a) => sum + a.amount, 0);
                                          newPreview[idx].totalAmount = newTotal;
                                          
                                          // Auto-update the movement amount if there is exactly 1 movement and we just added/removed an application
                                          if (newPreview[idx].newMovements?.length === 1) {
                                             newPreview[idx].newMovements[0].amount = newTotal;
                                          }
                                          
                                          setPreview(newPreview);
                                        }}
                                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all min-w-[2rem] ${
                                          isSelected ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/20' : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'
                                        }`}
                                      >
                                        {isSelected ? <CheckCircle2 size={16} /> : <Plus size={16} />}
                                      </button>
                                      <div>
                                        <div className="text-xs font-bold text-white">{d.number || d.locatorCode}</div>
                                        <div className="text-[10px] text-zinc-500">{d.date} • Saldo: {formatCurrency(d.remainingAmount)}</div>
                                      </div>
                                    </div>

                                    {isSelected && (
                                      <div className="flex items-center gap-2 mt-2 md:mt-0">
                                        <label className="text-[10px] font-bold text-emerald-500 uppercase whitespace-nowrap">A Aplicar:</label>
                                        <div className="relative w-full md:w-32">
                                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] font-bold text-zinc-500">Gs.</span>
                                          <input 
                                            type="number"
                                            value={app.amount}
                                            onChange={(e) => {
                                              const val = parseFloat(e.target.value) || 0;
                                              const newPreview = [...preview];
                                              const apps = newPreview[idx].applications || [];
                                              const appIdxToUpdate = apps.findIndex(a => a.documentId === d.id);
                                              if (appIdxToUpdate >= 0) {
                                                 apps[appIdxToUpdate].amount = Math.min(val, d.remainingAmount);
                                              }
                                              newPreview[idx].applications = apps;
                                              
                                              // Recalculate totalAmount based on applications
                                              const newTotal = apps.reduce((sum, a) => sum + a.amount, 0);
                                              newPreview[idx].totalAmount = newTotal;
                                              
                                              // Auto-update the movement amount if there is exactly 1 movement and we just added/removed an application
                                              if (newPreview[idx].newMovements?.length === 1) {
                                                 newPreview[idx].newMovements[0].amount = newTotal;
                                              }
                                              
                                              setPreview(newPreview);
                                            }}
                                            className="w-full bg-zinc-900 border border-emerald-500/20 rounded-lg pl-7 pr-2 py-1.5 text-xs font-mono font-bold text-emerald-400"
                                          />
                                        </div>
                                        <button 
                                          type="button"
                                          onClick={() => {
                                            const newPreview = [...preview];
                                            const apps = newPreview[idx].applications || [];
                                            const appIdxToUpdate = apps.findIndex(a => a.documentId === d.id);
                                            if (appIdxToUpdate >= 0) {
                                               apps[appIdxToUpdate].amount = d.remainingAmount;
                                            }
                                            newPreview[idx].applications = apps;
                                            
                                            // Recalculate totalAmount based on applications
                                            const newTotal = apps.reduce((sum, a) => sum + a.amount, 0);
                                            newPreview[idx].totalAmount = newTotal;
                                            
                                            // Auto-update the movement amount if there is exactly 1 movement and we just added/removed an application
                                            if (newPreview[idx].newMovements?.length === 1) {
                                               newPreview[idx].newMovements[0].amount = newTotal;
                                            }
                                            
                                            setPreview(newPreview);
                                          }}
                                          className="text-[9px] font-black text-indigo-400 hover:text-indigo-300 uppercase tracking-tighter"
                                        >
                                          Máx.
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              
                              {doc.contact?.id && documents.filter(d => {
                                const isCorrectDirection = doc.type === 'RECEIPT_EXPENSE' ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE') : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                                return (d.condition === 'CREDITO' || d.isHistorical) && d.remainingAmount > 0 && isCorrectDirection && d.contactId === doc.contact?.id;
                              }).length === 0 && (
                                <div className="text-center py-4 border border-dashed border-zinc-800 rounded-xl">
                                  <p className="text-[10px] text-zinc-500 italic">No hay facturas pendientes para este contacto.</p>
                                </div>
                              )}

                              {/* Manual Search Addition */}
                              <div className="mt-4 pt-4 border-t border-zinc-800">
                                <label className="text-[9px] font-bold text-zinc-500 uppercase tracking-widest px-1 mb-2 block">Agregar otra factura manualmente</label>
                                <select
                                  value=""
                                  onChange={(e) => {
                                    const docId = e.target.value;
                                    const d = documents.find(docs => docs.id === docId);
                                    if (d) {
                                      const newPreview = [...preview];
                                      const apps = newPreview[idx].applications || [];
                                      if (!apps.find(a => a.documentId === d.id)) {
                                        apps.push({ documentId: d.id, amount: d.remainingAmount });
                                        newPreview[idx].applications = apps;
                                        
                                        // Auto update total
                                        const newTotal = apps.reduce((sum, a) => sum + a.amount, 0);
                                        newPreview[idx].totalAmount = newTotal;
                                        if (newPreview[idx].newMovements?.length === 1) {
                                            newPreview[idx].newMovements[0].amount = newTotal;
                                        }
                                        
                                        // Update contact if not set
                                        if (!newPreview[idx].contact?.id) {
                                          newPreview[idx].contact = {
                                            name: d.contactName || '',
                                            id: d.contactId,
                                            isNew: false
                                          };
                                        }
                                        
                                        setPreview(newPreview);
                                      }
                                    }
                                  }}
                                  className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-xs text-zinc-300 focus:outline-none focus:border-emerald-500/50"
                                >
                                  <option value="">Buscar factura por número o nombre...</option>
                                  {documents
                                    .filter(d => {
                                      const isCorrectDirection = doc.type === 'RECEIPT_EXPENSE' 
                                        ? (d.type === 'PURCHASE_INVOICE' || d.type === 'INTERNAL_EXPENSE')
                                        : (d.type === 'SALES_INVOICE' || d.type === 'INTERNAL_INCOME');
                                      return (d.condition === 'CREDITO' || d.isHistorical) && d.remainingAmount > 0 && isCorrectDirection;
                                    })
                                    .map(d => (
                                      <option key={`generic-add-${d.id}`} value={d.id}>
                                        {d.contactName} - {d.number || d.locatorCode} (Saldo: {formatCurrency(d.remainingAmount)})
                                      </option>
                                    ))}
                                </select>
                              </div>

                         </div>
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
            </div>

            <div className="p-6 bg-zinc-950/50 border-t border-zinc-800">
               {preview.some(p => p.isDuplicate) && (
                 <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center justify-center gap-2 text-red-500 text-sm font-bold">
                   <AlertTriangle size={18} />
                   Corrige u omite los documentos duplicados para poder registrar.
                 </div>
               )}
               <button onClick={handleConfirm} disabled={registering || preview.some(p => p.isDuplicate)} className={`w-full py-4 text-white font-black rounded-2xl transition-all shadow-xl flex items-center justify-center gap-2 ${(registering || preview.some(p => p.isDuplicate)) ? 'bg-zinc-800 text-zinc-500 shadow-none' : 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20'}`}>
                 {registering ? <Loader2 className="animate-spin" /> : <><CheckCircle2 /> CONFIRMAR Y REGISTRAR TODO</>}
               </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {fallbackRequest && (
          <AIFallbackModal
            request={fallbackRequest}
            onConfirm={() => {
              fallbackResolver.current?.(true);
              setFallbackRequest(null);
            }}
            onCancel={() => {
              fallbackResolver.current?.(false);
              setFallbackRequest(null);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};
